import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { firestore, aiModel } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { userState } from "../../atom/common";
import { useRecoilValue } from "recoil";
import Divider from "@mui/material/Divider";
import SEO from "../../components/seo";

const GameItemUpload = () => {
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [gold, setGold] = useState('');
  const [userId, setUserId] = useState('');
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const loginUser = useRecoilValue(userState)

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        if (reader.result) {
          resolve(reader.result.split(',')[1]);
        } else {
          reject(new Error("File reading failed"));
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onDrop = useCallback(async acceptedFiles => {
    const file = acceptedFiles[0];
    setImageUrl(URL.createObjectURL(file));
    if (file) {
      try {
        // 이미지 압축 및 크기 조정
        const compressedFile = await compressImage(file);

        // 이미지를 흑백으로 변환
        const blackAndWhiteImageFile = await convertToBlackAndWhite(compressedFile);

        const base64String = await toBase64(blackAndWhiteImageFile);
        setFile(base64String);
      } catch (error) {
        console.error("Error processing image:", error);
      }
    }
  }, []);

  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // 이미지 크기를 최대 800px로 조정
          const maxWidth = 600;
          const scaleFactor = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * scaleFactor;

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // 이미지 품질을 0.1로 설정하여 압축
          canvas.toBlob(blob => {
            resolve(new File([blob], file.name, {type: 'image/jpeg'}));
          }, 'image/jpeg', 0.1);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // 이미지를 흑백으로 변환하는 함수
  const convertToBlackAndWhite = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;

          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;

          // 이미지 데이터를 흑백으로 변환
          for (let i = 0; i < data.length; i += 4) {
            const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
            data[i] = avg;
            data[i + 1] = avg;
            data[i + 2] = avg;
          }

          ctx.putImageData(imageData, 0, 0);

          canvas.toBlob(blob => {
            resolve(new File([blob], file.name, {type: 'image/jpeg'}));
          }, 'image/jpeg');
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const {getRootProps, getInputProps} = useDropzone({onDrop});

  const handleGoldChange = (event) => {
    setGold(event.target.value);
  };

  const handleUserIdChange = (event) => {
    setUserId(event.target.value);
  };

  const handleTitleChange = (event) => {
    if (event.target.value.length <= 100) {
      setTitle(event.target.value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setUploading(true);
    if (file && gold && userId && title) {
      try {
        const success = await addItem();
        if (success) {
          setOpenSnackbar(true);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('Upload failed!');
      } finally {
        setUploading(false);
        setFile(null);
        setImageUrl(null);
        setGold('');
        setUserId('');
        setTitle('');
      }
    } else {
      alert('Please fill in all fields.');
    }
  };

  const addItem = async () => {
    try {
      const req = {
        contents: [
          {
            role: 'user', parts: [{
              inlineData: {
                mimeType: 'image/jpeg',
                data: `${file}`
              }
            }, {text: `아이템 옵션 알려줘, 개행 문자는 <br>을 사용해줘`}]
          }
        ],
      };
      const aiResult = await aiModel.generateContent(req);
      const fireStoreRef = collection(firestore, `items`);
      await addDoc(fireStoreRef, {
        'createdBy': getAuth().currentUser.uid,
        'userId': loginUser.email,
        'gameId': userId,
        'title': title,
        'gold': gold,
        'content': `${title}<br>${aiResult.response.text()}`,
        'createdAt': serverTimestamp(),
        'completed': false,
      })
      return true;
    } catch (error) {
      alert('로그인이 필요한 서비스 입니다.');
    }
    return false;
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <Grid container justifyContent="center" sx={{mt: 5}}>
      <SEO
        title="디아블로 아이템 등록 - 아이템을 쉽게 판매하세요"
        description="디아블로 아이템을 등록하고 판매하세요. 이미지 한장으로 빠르게 아이템을 거래할 수 있습니다."
        keywords="디아블로 아이템 등록, 아이템 판매, 게임 아이템 거래, 디아블로 장비 판매"
      />
      <Grid item xs={12} sm={8} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              아이템 등록
            </Typography>
            <TextField
              fullWidth
              label="제목 (최대 60자)"
              value={title}
              onChange={handleTitleChange}
              variant="outlined"
              margin="normal"
              inputProps={{ maxLength: 60 }}
              helperText={`${title.length}/60`}
            />
            <TextField
              fullWidth
              label="User ID"
              value={userId}
              onChange={handleUserIdChange}
              variant="outlined"
              margin="normal"
            />
            <TextField
              fullWidth
              label="Price (gold)"
              type="number"
              value={gold}
              onChange={handleGoldChange}
              variant="outlined"
              margin="normal"
            />
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #888',
                padding: 2,
                textAlign: 'center',
                cursor: 'pointer',
                mt: 2,
                mb: 2,
                borderRadius: 1
              }}
            >
              <input {...getInputProps()} />
              <Typography>
                게임 아이템 이미지를 여기에 드래그 앤 드롭하거나 클릭하여 선택하세요
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={uploading || !title || !gold || !userId || !imageUrl || !loginUser.email}
            >
              {uploading ? '등록 중...' : '등록하기'}
              {uploading && <CircularProgress size={24} sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }} />}
            </Button>
            {!loginUser.email && (
              <Typography color="error" sx={{ mt: 2 }}>
                아이템을 등록하려면 로그인이 필요합니다.
              </Typography>
            )}

            {imageUrl && (
              <Box sx={{textAlign: 'center', mb: 2}}>
                <Divider style={{marginTop: 10, marginBottom: 10}}/>
                <img src={imageUrl} alt="Preview" style={{maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: 5}}/>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message="아이템 등록이 완료되었습니다!"
      />
    </Grid>
  );
};

export default GameItemUpload;
